import { runBootSequence } from "@yjsgames/boot";
import type { GameBoot, TabContentLayout } from "../boot/BootTypes.js";
import { BOOT_SEQUENCE } from "../boot/bootSequence.js";
import { APP_VERSION } from "../env/appFlavor.js";
import type { AccountLinkProvider } from "@yjsgames/account-link";
import {
  NOOP_SUPPORT_SHARE,
  type SupportShareProvider,
} from "@yjsgames/support-contact";

export interface GameSceneBootDeps
  extends Omit<
    GameBoot,
    | "api"
    | "addDisposer"
    | "version"
    | "accountLink"
    | "supportShare"
    | "collectSupportLog"
    | "setAccountNeedsLink"
    | "layout"
    | "dailyResets"
  > {
  readonly apiRegister: GameBoot["api"]["register"];
  readonly addDisposer: GameBoot["addDisposer"];
  readonly registryGet: (key: string) => unknown;
  readonly tabContent: TabContentLayout;
  readonly shellLayout: GameBoot["layout"]["shell"];
  readonly setAccountNeedsLink?: GameBoot["setAccountNeedsLink"];
  readonly registerDailyReset: (key: string, onReset: () => void) => void;
}

/** 도메인 boot 설치자 실행용 GameBoot 계약 조립. */
export function runGameSceneBoot(deps: GameSceneBootDeps): void {
  const boot: GameBoot = {
    scene: deps.scene,
    ctx: deps.ctx,
    tabs: deps.tabs,
    api: { register: deps.apiRegister },
    addDisposer: deps.addDisposer,
    modals: deps.modals,
    screen: deps.screen,
    openTab: deps.openTab,
    settings: deps.settings,
    version: APP_VERSION,
    cheats: deps.cheats,
    cheatForceHouseAd: deps.cheatForceHouseAd,
    togglePerfOverlay: deps.togglePerfOverlay,
    perfOverlayEnabled: deps.perfOverlayEnabled,
    rewardedAd: deps.rewardedAd,
    snapshot: deps.snapshot,
    ui: deps.ui,
    battleDialogue: deps.battleDialogue,
    commands: deps.commands,
    guideQuest: deps.guideQuest,
    guideQuestHighlight: deps.guideQuestHighlight,
    dailyReset: deps.dailyReset,
    cooldown: deps.cooldown,
    dailyResets: { register: deps.registerDailyReset },
    dungeon: deps.dungeon,
    resetSave: deps.resetSave,
    accountLink: () =>
      (deps.registryGet("accountLink") as AccountLinkProvider | undefined) ?? null,
    supportShare: () =>
      (deps.registryGet("supportShare") as SupportShareProvider | undefined) ??
      NOOP_SUPPORT_SHARE,
    collectSupportLog: () =>
      (deps.registryGet("collectSupportLog") as (() => string) | undefined)?.() ?? "",
    setAccountNeedsLink: deps.setAccountNeedsLink ?? (() => {}),
    combatSpecSummary: deps.combatSpecSummary,
    provideBattleProc: deps.provideBattleProc,
    tutorial: deps.tutorial,
    tutorialGate: deps.tutorialGate,
    provideSettingsToggle: deps.provideSettingsToggle,
    layout: {
      tabContent: deps.tabContent,
      shell: deps.shellLayout,
    },
    ports: deps.ports,
    rewards: deps.rewards,
    features: deps.features,
    state: deps.state,
    progression: deps.progression,
  };

  runBootSequence(boot, BOOT_SEQUENCE, {
    isEnabled: (key) => key !== "dungeon" || deps.features.dungeon,
  });
}
